import { create, type StoreApi } from "zustand";
import type { Node } from "interfaces/schemas";
import type { FetchError } from "utils";
import { isUUID } from "utils";
import { buildTree } from "utils/treeUtils";
import { useUser } from "stores/user";
import mandatorUtils from "utils/mandators";
import type { RefObject } from "react";
import { ProjectQry } from "api/queries";
import type { ResultOf } from "gql.tada";
import type { ProjectActions } from "./projectActions";
import { createProjectActions } from "./projectActions";
import { gqlFetchWithAuth } from "utils/fetchData/gqlFetchWithAuth";
import type { TreeApi } from "@hogartrabe/react-arborist-vrh";
import { useProjectUIStore } from "stores/project";
import { Unit } from "variables/enums";

type ProjectRaw = ResultOf<typeof ProjectQry>["project"];
export type Project = Omit<NonNullable<ProjectRaw>, "projectNodes">;
export type ProjectNodes = NonNullable<ProjectRaw>["projectNodes"];

export type TreeRef = RefObject<TreeApi<Node> | null>;

export type ProjectStore = {
  treeRef: TreeRef | null;
  project: Project | null;
  tree: Node[] | null;
  actions: ProjectActions;
  isLoading: boolean;
  error: FetchError | Error | null;
  fetch: (projectId: string) => Promise<void>;
  refetch: () => void;
  init: (ref: TreeRef) => Promise<void>;
  clear: () => void;
  set: (projectId: string) => void;
};

const useProjectStore = create<ProjectStore>()((set, get) => ({
  treeRef: null,
  project: null,
  tree: null,
  actions: createProjectActions(set, get),
  isLoading: false,
  error: null,
  clear: () => set({ project: null, tree: null, error: null }),
  fetch: async (projectId: string) => {
    if (!projectId) {
      console.error("Project ID is missing. Cannot fetch project data.");
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const { project } = await gqlFetchWithAuth({
        query: ProjectQry,
        variables: { input: { id: projectId } },
      });
      if (!project) {
        throw new Error("Project data is missing in the response");
      }
      if (!project.title) {
        throw new Error("Project title is missing in the response");
      }
      const tree = buildTree(project.projectNodes, project.id, project.title);
      useProjectUIStore.getState().initOpenMap(tree);
      set({
        project: project,
        tree,
      });
    } catch (error) {
      const err = error as FetchError;
      set({ error: err });
    } finally {
      set({ isLoading: false });
    }
  },
  refetch: () => {
    const { project } = get();
    if (project?.id) {
      get().fetch(project.id);
    }
  },
  set: (projectId: string) => {
    if (!isUUID(projectId)) {
      set({ error: new Error("Invalid project ID") });
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("projectId", projectId);
    const newUrl = window.location.pathname + "?" + searchParams.toString();
    window.history.pushState(null, "", newUrl);

    get().fetch(projectId);
  },
  init: async (ref) => {
    const searchParams = new URLSearchParams(window.location.search);
    const projectId = searchParams.get("projectId");
    set({ treeRef: ref });
    if (!projectId || !isUUID(projectId)) {
      set({
        error: new Error("Invalid project ID"),
      });
      return;
    }
    await get().fetch(projectId);
  },
}));

export const useProjectActions = () => {
  const tree = useProjectStore((state) => state.actions);
  const refetch = useProjectStore((state) => state.refetch);
  const set = useProjectStore((state) => state.set);
  const clear = useProjectStore((state) => state.clear);
  return { tree, refetch, set, clear };
};

export const useIsProjectLoading = () =>
  useProjectStore((state) => state.isLoading);
export const useProject = () => useProjectStore((state) => state.project);
export const getProject = () => useProjectStore.getState().project;
export const useProjectTree = () => useProjectStore((state) => state.tree);
export const initProjectStore = (treeRef: RefObject<TreeApi<Node> | null>) =>
  useProjectStore.getState().init(treeRef);
export const useTreeRef = () => useProjectStore((state) => state.treeRef);
export const useProjectError = () => useProjectStore((state) => state.error);

export const getProjectUnit = () => {
  const project = useProjectStore.getState().project;
  switch (project?.unit?.unit) {
    case Unit.de_m3ph.toString():
      return Unit.de_m3ph;
    case Unit.de_lps.toString():
      return Unit.de_lps;
    case Unit.de_us.toString():
      return Unit.de_us;
    default:
      return null;
  }
};

export const useProjectUnit = () => {
  const project = useProjectStore((state) => state.project);
  switch (project?.unit?.unit) {
    case Unit.de_m3ph.toString():
      return Unit.de_m3ph;
    case Unit.de_lps.toString():
      return Unit.de_lps;
    case Unit.de_us.toString():
      return Unit.de_us;
    default:
      return null;
  }
};

export const getTreeApi = () => {
  const treeRef = useProjectStore.getState().treeRef;
  return treeRef?.current || null;
};

export const useProjectPartners = () => {
  const mandator = useProjectStore((state) => state.project?.mandator);
  const user = useUser();

  if (mandator && user?.tenant) {
    try {
      const parsedTenant = mandatorUtils.parseCountryCode(user.tenant);
      const parsedMandatorKey = mandatorUtils.parseMandatorKey(mandator);
      return mandatorUtils
        .getPartnersByMandator(parsedMandatorKey, parsedTenant)
        .join(", ");
    } catch (error) {
      console.error(error);
    }
  }
  return null;
};

interface AddedProductState {
  addedProducts: string[];
  setAddedProducts: (addedProduct: string) => void;
  hasAddedNode: boolean;
  setHasAddedNode: () => void;
  reset: () => void;
}

const useAddedProductStore = create<AddedProductState>()(
  (set, _get, api: StoreApi<AddedProductState>) => ({
    addedProducts: [],
    hasAddedNode: false,
    setAddedProducts: (newProduct) =>
      set((state) => ({
        addedProducts: [...state.addedProducts, newProduct],
      })),
    setHasAddedNode: () => set({ hasAddedNode: true }),

    reset: () => set(api.getInitialState()),
  }),
);

export const useHasAddedNode = () =>
  useAddedProductStore((state) => state.hasAddedNode);
export const useAddedProducts = () =>
  useAddedProductStore((state) => state.addedProducts);
export const useSetAddedProducts = () =>
  useAddedProductStore((state) => state.setAddedProducts);
export const useSetHasAddedNode = () =>
  useAddedProductStore((state) => state.setHasAddedNode);
export const useResetAddedProductStore = () =>
  useAddedProductStore((state) => state.reset);
export const refetchProject = () => useProjectStore.getState().refetch();
